import { Component, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { currencyLabel } from "../lib/currencyDisplay";
import { ChevronDown, ChevronRight, Plus, RadioTower, Save, Settings as SettingsIcon, Sparkles, Trash2, Wand2, ScrollText, HelpCircle, Upload } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["free", "paid"];
const QUEST_TYPES = ["combat", "gathering", "arcane", "exploration", "social", "seasonal", "event"];
const EVENT_TYPES = ["gather_material", "scavenge", "merchant", "chest", "blessing", "curse", "mini_encounter", "community_goal"];
const SHOP_TYPES = ["item", "loot_crate", "material_pack"];
const GEAR_SLOTS = ["weapon", "head", "body", "trinket"];
const ITEM_TYPES = ["weapon", "head", "body", "trinket", "consumable"];
const DEFAULT_NAME_PROMPTS = {
  weapon: "Name weapons as practical, dangerous gear that fits this loot style.",
  head: "Name head gear as helmets, visors, hoods, caps, or masks that fit this loot style.",
  body: "Name body gear as armor, guards, coats, vests, or harnesses that fit this loot style.",
  trinket: "Name trinkets as charms, badges, seals, tokens, or relics that fit this loot style.",
  consumable: "Name consumables as food, rations, tonics, potions, salves, or emergency supplies. Do not use gear materials.",
};
const STATS = ["attack", "defense", "strength", "magic", "scavenging", "tinkering"];
const RARITIES = ["common", "uncommon", "rare", "epic", "legendary"];
const DEFAULT_CONSUMABLE_HEALING = {
  common: [8, 14],
  uncommon: [24, 40],
  rare: [60, 95],
  epic: [110, 170],
  legendary: [200, 300],
};
const ENCOUNTER_KINDS = ["enemy", "hazard"];
const TIERS = ["1", "2", "3", "4"];
const DUNGEON_TOKEN_KEY = "dungeon_tokens";
const LEGACY_TOKEN_VALUES = { [DUNGEON_TOKEN_KEY]: 1, copper: 1, silver: 10, electrum: 50, gold: 100, platinum: 1000 };
const tokenValue = (values = {}) => Object.entries(values || {}).reduce((total, [key, raw]) => {
  const multiplier = LEGACY_TOKEN_VALUES[key] || 0;
  const amount = Array.isArray(raw) ? Number(raw[0] || 0) : Number(raw || 0);
  return total + multiplier * amount;
}, 0);
const tokenRangeValue = (values = {}) => Object.entries(values || {}).reduce((range, [key, raw]) => {
  const multiplier = LEGACY_TOKEN_VALUES[key] || 0;
  const pair = Array.isArray(raw) ? raw : [raw, raw];
  return [
    range[0] + multiplier * Number(pair[0] || 0),
    range[1] + multiplier * Number(pair[1] || 0),
  ];
}, [0, 0]);
const tokenText = (values = {}) => {
  const amount = tokenValue(values);
  return amount > 0 ? `${amount} Dungeon Tokens` : "Free";
};

const TABS = [
  { id: "settings", label: "Settings", icon: SettingsIcon },
  { id: "shop", label: "Shop", icon: Sparkles },
  { id: "events", label: "Events", icon: RadioTower },
  { id: "overlay", label: "Overlay", icon: SettingsIcon },
  { id: "quests", label: "Special Quests", icon: ScrollText },
  { id: "skills", label: "Skills", icon: Wand2 },
  { id: "rarities", label: "Rarities", icon: Sparkles },
];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const mediaUrl = (url) => !url ? "" : String(url).startsWith("http") ? url : `${BACKEND_URL}${url}`;
const deepClone = (value) => JSON.parse(JSON.stringify(value || {}));
const errorText = (error, fallback = "Save failed") => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(item => item?.msg || item?.message || JSON.stringify(item)).join("; ");
  }
  if (typeof detail === "object") return detail.msg || detail.message || JSON.stringify(detail);
  return String(detail);
};

const blankQuest = {
  id: "", title: "New Special Quest", slug: "", quest_type: "event", difficulty: "Standard", recommended_level: 1, duration_seconds: 90,
  description: "A limited-time or hand-built RPG quest for viewers to join.",
  artwork_reference: "cozy fantasy barracks, readable action, stream overlay friendly",
  artwork_url: "",
  entry_cost: 0, currency_id: "free", daily_free: false, limit_per_user_per_stream: false,
  event_quest: true, event_active: true,
  min_party: 1, max_party: 9999, join_window_seconds: 60,
  encounter_count: 4, encounter_tiers: [1, 1, 2, 2], loot_rolls: 1,
  ai_directives: {
    enemy_brief: "Describe the enemies and hazards this quest should generate.",
    gear_brief: "Describe the loot style, materials, and naming theme.",
    encounter_brief: "Describe the rooms, obstacles, and pacing.",
  },
  loot_generation: {
    enabled: true,
    rolls: 1,
    rarity_weights: { common: 70, uncommon: 25, rare: 5 },
    slots: ["weapon", "head", "body", "trinket", "consumable"],
    prefixes: ["Bent", "Scuffed", "Patchwork"],
    materials: ["Tin", "Wire", "Scrap"],
    name_parts_by_slot_rarity: {
      weapon: {
        common: { prefixes: ["Bent", "Scuffed"], materials: ["Tin", "Scrap Iron"] },
        uncommon: { prefixes: ["Reinforced", "Balanced"], materials: ["Steel", "Brass"] },
        rare: { prefixes: ["Runed", "Veteran"], materials: ["Mythril", "Crystal"] },
        epic: { prefixes: ["Heroic", "Stormforged"], materials: ["Star Iron", "Aethersteel"] },
        legendary: { prefixes: ["Legendary", "Crowned"], materials: ["Sunsteel", "Royal Mythril"] },
      },
      head: {
        common: { prefixes: ["Dented", "Scuffed"], materials: ["Tin", "Cloth"] },
        uncommon: { prefixes: ["Reinforced", "Fitted"], materials: ["Leather", "Brass"] },
        rare: { prefixes: ["Runed", "Keen"], materials: ["Crystal", "Silvered Steel"] },
        epic: { prefixes: ["Oracle", "Stormguard"], materials: ["Aetherglass", "Star Iron"] },
        legendary: { prefixes: ["Crowned", "Eternal"], materials: ["Sunsteel", "Royal Mythril"] },
      },
      body: {
        common: { prefixes: ["Patched", "Worn"], materials: ["Leather", "Canvas"] },
        uncommon: { prefixes: ["Reinforced", "Layered"], materials: ["Boiled Leather", "Chain"] },
        rare: { prefixes: ["Runed", "Veteran"], materials: ["Mythril Mesh", "Silvered Plate"] },
        epic: { prefixes: ["Oathbound", "Stormguard"], materials: ["Aetherplate", "Starsteel"] },
        legendary: { prefixes: ["Crowned", "Unbroken"], materials: ["Royal Mythril", "Sunsteel"] },
      },
      trinket: {
        common: { prefixes: ["Cracked", "Faded"], materials: ["Wooden", "Tin"] },
        uncommon: { prefixes: ["Lucky", "Polished"], materials: ["Brass", "Amber"] },
        rare: { prefixes: ["Runed", "Glimmering"], materials: ["Crystal", "Silver"] },
        epic: { prefixes: ["Oracle", "Aetherlit"], materials: ["Aetherglass", "Starlight"] },
        legendary: { prefixes: ["Crowned", "Eternal"], materials: ["Sunstone", "Royal Mythril"] },
      },
      consumable: {
        common: { prefixes: ["Minor", "Simple"], nouns: ["Potion", "Elixir", "Draught"] },
        uncommon: { prefixes: ["Steady", "Brewed"], nouns: ["Potion", "Tonic", "Tea"] },
        rare: { prefixes: ["Greater", "Bright"], nouns: ["Potion", "Elixir", "Poultice"] },
        epic: { prefixes: ["Potent", "Aether"], nouns: ["Potion", "Ambrosia", "Draught"] },
        legendary: { prefixes: ["Legendary", "Miracle"], nouns: ["Elixir", "Panacea", "Ambrosia"] },
      },
    },
    name_parts_by_rarity: {
      common: { prefixes: ["Bent", "Scuffed"], materials: ["Tin", "Scrap"] },
      uncommon: { prefixes: ["Reinforced", "Lucky"], materials: ["Brass", "Leather"] },
      rare: { prefixes: ["Runed", "Veteran"], materials: ["Mythril", "Crystal"] },
      epic: { prefixes: ["Heroic", "Ancient"], materials: ["Star Iron", "Aetherglass"] },
      legendary: { prefixes: ["Legendary", "Crowned"], materials: ["Sunsteel", "Royal Mythril"] },
    },
    nouns: { weapon: ["Shiv"], head: ["Cap"], body: ["Vest"], trinket: ["Charm"], consumable: ["Potion", "Elixir"] },
    allowed_stats_by_slot: { weapon: ["attack"], head: ["defense"], body: ["defense"], trinket: ["magic"] },
    name_prompts_by_type: DEFAULT_NAME_PROMPTS,
  },
  encounter_generation: {
    enabled: true,
    kind: "enemy",
    enemy_prefixes: ["Rusty"],
    enemy_nouns: ["Training Dummy"],
    skill_weights: { attack: 40, defense: 20, scavenging: 20, tinkering: 20 },
  },
  reward_config: { xp: { attack: 10 }, wallet: { [DUNGEON_TOKEN_KEY]: 10 }, materials: {} },
  xp_per_encounter: 25, enabled: true,
};

const blankEvent = {
  id: "", title: "Supply Cache", event_type: "gather_material",
  announcement_text: "A supply cache rattles open near the barracks. Type !gather before it vanishes.",
  command_keyword: "gather", duration_seconds: 90,
  ai_directives: {
    event_brief: "A quick chat-wide popup with a simple command and no player cap.",
    reward_brief: "Small Dungeon Tokens, useful materials, and a little skill XP.",
  },
  reward_generation: {
    enabled: true,
    skill_weights: { scavenging: 70, tinkering: 30 },
    xp: [4, 10],
    wallet: { [DUNGEON_TOKEN_KEY]: [3, 12] },
    materials: { scrap: [1, 3], loose_parts: [0, 2] },
  },
  reward_config: {}, enabled: true,
};

const blankShopItem = {
  id: "", name: "Health Potion", description: "A useful potion bought with Dungeon Tokens.",
  shop_type: "item",
  image_url: "",
  item: { name: "Health Potion", rarity: "uncommon", item_type: "consumable", slot: null, stats: { heal: 45 }, flavor: "Standard barracks red. Gets the job done.", consumable: true, sell_value: { [DUNGEON_TOKEN_KEY]: 8 } },
  loot_generation: deepClone(blankQuest.loot_generation),
  ai_directives: { gear_brief: "Shop crate loot with a practical barracks theme." },
  materials: {},
  price: { [DUNGEON_TOKEN_KEY]: 50 },
  enabled: true,
};

export default function RPGAdmin() {
  const [tab, setTab] = useState("settings");
  const activeGuide = GUIDES[tab] || GUIDES.quests;
  return (
    <div className="space-y-8 text-base" data-testid="rpg-admin-page">
      <div className="max-w-6xl">
        <div className="label-eyebrow">Barracks RPG</div>
        <h1 className="text-4xl font-semibold tracking-tight mt-1">RPG Admin</h1>
        <p className="text-lg text-[hsl(var(--muted-foreground))] mt-2">
          Control the simple !quest flow, shop, chat-wide events, and long-term RPG balance.
        </p>
      </div>

      <Guide guide={activeGuide} />

      <div className="flex flex-wrap gap-2 surface-2 p-2 rounded w-fit" data-testid="rpg-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`rpg-tab-${t.id}`} className={`text-sm uppercase tracking-widest px-5 py-3 rounded flex items-center gap-2 ${tab === t.id ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))] hover:text-white"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "quests" && <QuestEditor />}
      {tab === "events" && <EventEditor />}
      {tab === "skills" && <SimpleCrud collection="skills" columns={SKILL_COLS} blank={blankSkill} />}
      {tab === "rarities" && <SimpleCrud collection="rarities" columns={RARITY_COLS} blank={blankRarity} />}
      {tab === "shop" && <EditorErrorBoundary><ShopEditor /></EditorErrorBoundary>}
      {tab === "overlay" && <RPGOverlaySettings />}
      {tab === "settings" && <RPGSettings />}
    </div>
  );
}

const GUIDES = {
  quests: {
    title: "Special quests are optional.",
    body: "The main viewer flow is generated from !quest in chat. Use this page only for seasonal, limited-time, or hand-built event quests you want to list with !quest list.",
    points: [
      { label: "Simple quests", text: "Configure the normal !quest free, !quest bunny, and !quest rat flow in Settings." },
      { label: "Special use", text: "Use this editor for Christmas quests, one-off events, and custom seasonal loot tables." },
      { label: "Visibility", text: "Turn active/visible off after the event; earned loot stays in inventories." },
    ],
  },
  events: {
    title: "Events are short chat-wide popups.",
    body: "Random events appear in chat on the settings cadence. Viewers type the event command while it is live. There is no party cap.",
    points: [
      { label: "Command", text: "The chat word viewers type without the exclamation mark." },
      { label: "Window", text: "How long the event command stays open." },
      { label: "Rewards", text: "Each participant rolls from the event reward ranges." },
    ],
  },
  skills: {
    title: "Skills are RPG stats.",
    body: "Quest enemies, hazards, and events award XP to these skill ids.",
    points: [
      { label: "ID", text: "Stable lowercase key used by generated rules." },
      { label: "Max level", text: "Caps long-term progression." },
      { label: "Color/icon", text: "Used on the viewer RPG page." },
    ],
  },
  rarities: {
    title: "Rarities control loot display and odds.",
    body: "The detailed stat and sell ranges are in Settings. This tab is mostly for labels, colors, and fallback weights.",
    points: [
      { label: "Color", text: "Used for gear icons and item borders." },
      { label: "Weight", text: "Fallback rarity odds." },
      { label: "Balance", text: "Tune the actual item/enemy ranges in Settings." },
    ],
  },
  settings: {
    title: "Settings control pacing and balance.",
    body: "Use these controls to keep progression slow, AI usage capped, rare loot exciting, and random events predictable.",
    points: [
      { label: "AI", text: "All RPG AI narration is one short sentence with daily caps." },
      { label: "Gear", text: "Stat ranges are global by slot and rarity." },
      { label: "Events", text: "Random events rotate on interval and chance." },
    ],
  },
  shop: {
    title: "Shop stock is bought with Dungeon Tokens.",
    body: "Use the shop for consumables and future Dungeon Token sinks. Prices should be balanced against quest rewards and gear sell values.",
    points: [
      { label: "Consumables", text: "Health potions can be used manually from the character sheet." },
      { label: "Pricing", text: "Bigger potions should be a better deal per HP but still require saving more Dungeon Tokens." },
      { label: "Enabled", text: "Disable an item to hide it without deleting it." },
    ],
  },
};

const blankSkill = { id: "", name: "", icon: "Box", color: "#9CA3AF", description: "", max_level: 99 };
const blankRarity = { id: "", name: "", color: "#9CA3AF", weight: 10 };
const SKILL_COLS = [
  { key: "id", label: "ID", mono: true },
  { key: "name", label: "Name" },
  { key: "color", label: "Color", type: "color" },
  { key: "icon", label: "Icon" },
  { key: "max_level", label: "Max Level", type: "number" },
  { key: "description", label: "Description" },
];
const RARITY_COLS = [
  { key: "id", label: "ID", mono: true },
  { key: "name", label: "Name" },
  { key: "color", label: "Color", type: "color" },
  { key: "weight", label: "Weight", type: "float" },
];

class EditorErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error("RPG editor render failed", error);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="surface p-5 max-w-6xl">
          <div className="text-lg font-semibold text-[hsl(var(--destructive))]">This RPG editor section hit a display error.</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            The rest of the admin page is still available. Refresh after saving, or check the last edited row for invalid data.
          </p>
          <pre className="mt-3 max-w-full overflow-auto rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] p-3 text-xs text-[hsl(var(--muted-foreground))]">
            {this.state.error?.message || String(this.state.error || "Unknown display error")}
          </pre>
          <button type="button" onClick={() => this.setState({ error: null })} className="mt-4 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm px-4 py-2 rounded">
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Guide({ guide }) {
  return (
    <div className="surface p-5 max-w-6xl">
      <div className="flex items-start gap-3">
        <HelpCircle className="w-5 h-5 mt-1 text-[hsl(var(--primary))]" />
        <div>
          <div className="text-lg font-semibold">{guide.title}</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 leading-6">{guide.body}</p>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
            {guide.points.map(point => (
              <div key={point.label} className="surface-2 p-3">
                <div className="label-eyebrow">{point.label}</div>
                <div className="text-sm mt-1 leading-6 text-[hsl(var(--muted-foreground))]">{point.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestEditor() {
  return (
    <div className="space-y-4">
      <FormNote title="Special quests only">
        Normal viewer quests are generated from !quest free, !quest bunny500, or !quest rat100. Use this editor only when you want a seasonal or hand-built quest to appear in !quest list.
      </FormNote>
      <ExpandableEditor collection="quests" blank={blankQuest} makeBlank={(row) => ({ ...blankQuest, ...row, max_party: row.max_party || 9999 })} renderSummary={QuestSummary} renderForm={QuestForm} />
    </div>
  );
}

function EventEditor() {
  return <ExpandableEditor collection="rpg-events" blank={blankEvent} makeBlank={(row) => ({ ...blankEvent, ...row })} renderSummary={EventSummary} renderForm={EventForm} canActivate />;
}

function ShopEditor() {
  return <ExpandableEditor collection="rpg-shop" blank={blankShopItem} makeBlank={makeShopDraft} renderSummary={ShopSummary} renderForm={ShopForm} />;
}

function makeShopDraft(row = {}) {
  return {
    ...deepClone(blankShopItem),
    ...row,
    item: { ...deepClone(blankShopItem.item), ...(row.item || {}) },
    loot_generation: { ...deepClone(blankShopItem.loot_generation), ...(row.loot_generation || {}) },
    ai_directives: { ...deepClone(blankShopItem.ai_directives), ...(row.ai_directives || {}) },
    materials: { ...(row.materials || {}) },
    price: { ...(row.price || {}) },
  };
}

function ExpandableEditor({ collection, blank, makeBlank, renderSummary: Summary, renderForm: Form, canActivate = false }) {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState("");
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/${collection}`);
    setRows(data);
  }, [collection]);
  useEffect(() => { load(); }, [load]);

  const startAdd = () => {
    const id = `new-${Date.now()}`;
    setDrafts({ ...drafts, [id]: makeBlank ? makeBlank({ id }) : { ...deepClone(blank), id } });
    setOpenId(id);
  };
  const setDraft = (id, patch) => setDrafts(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  const save = async (id) => {
    const draft = normalizeBeforeSave(drafts[id] || {});
    try {
      if (String(id).startsWith("new-")) {
        const payload = { ...draft };
        delete payload.id;
        await api.post(`/admin/${collection}`, payload);
        toast.success("Created");
      } else {
        const { id: publicId, _id, ...payload } = draft;
        await api.patch(`/admin/${collection}/${publicId || _id}`, payload);
        toast.success("Saved");
      }
      setOpenId("");
      setDrafts({});
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete ${row.title || row.name || row.id}?`)) return;
    await api.delete(`/admin/${collection}/${row.id || row._id}`);
    toast.success("Deleted");
    load();
  };
  const activate = async (row) => {
    await api.post(`/admin/${collection}/${row.id || row._id}/activate`);
    toast.success("Activated");
    load();
  };
  const allRows = [...rows, ...Object.values(drafts).filter(d => String(d.id).startsWith("new-"))];

  return (
    <div className="space-y-3 max-w-7xl">
      <div className="flex items-center justify-between">
        <span className="text-sm uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{rows.length} {collection}</span>
        <button onClick={startAdd} className="text-sm uppercase tracking-widest border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-5 py-3 rounded flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      {allRows.map(row => {
        const id = row.id || row._id;
        const opened = openId === id;
        const draft = drafts[id] || makeBlank(row);
        return (
          <div key={id} className="surface overflow-hidden">
            <button className="w-full text-left p-4 flex items-center gap-3 hover:bg-[hsl(var(--surface-2))]" onClick={() => {
              setOpenId(opened ? "" : id);
              if (!drafts[id]) setDraft(id, makeBlank(row));
            }}>
              {opened ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              <Summary row={row} />
            </button>
            {opened && (
              <div className="border-t border-[hsl(var(--border))] p-5 space-y-5">
                <Form value={draft} onChange={(patch) => setDraft(id, patch)} />
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => save(id)} className="bg-[hsl(var(--primary))] text-white text-sm px-5 py-3 rounded flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
                  {canActivate && !String(id).startsWith("new-") && <button onClick={() => activate(row)} className="border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm px-5 py-3 rounded flex items-center gap-2"><RadioTower className="w-4 h-4" /> Activate now</button>}
                  {!String(id).startsWith("new-") && <button onClick={() => del(row)} className="border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] text-sm px-5 py-3 rounded flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete</button>}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function normalizeBeforeSave(draft) {
  if ("quest_type" in draft) {
    const { repeat_entry_cost, repeat_currency_id, ...cleaned } = draft;
    return { ...cleaned, name: draft.title, min_party: 1, max_party: 9999, event_key: draft.event_quest ? (draft.event_key || draft.slug || "") : "" };
  }
  if ("item" in draft && "price" in draft) {
    const shopType = draft.shop_type || "item";
    if (shopType === "loot_crate") {
      const { item, materials, ...payload } = draft;
      return {
        ...payload,
        shop_type: shopType,
        loot_generation: {
          ...(draft.loot_generation || {}),
          enabled: true,
          rolls: 1,
          slots: (draft.loot_generation?.slots || ITEM_TYPES).filter(type => ITEM_TYPES.includes(type)),
          name_prompts_by_type: {
            ...DEFAULT_NAME_PROMPTS,
            ...((draft.loot_generation || {}).name_prompts_by_type || {}),
          },
        },
        ai_directives: draft.ai_directives || {},
      };
    }
    if (shopType === "material_pack") {
      const { item, loot_generation, ai_directives, ...payload } = draft;
      return { ...payload, shop_type: shopType, materials: draft.materials || {} };
    }
    const item = { ...(draft.item || {}), name: (draft.item || {}).name || draft.name };
    const itemType = item.item_type || (item.consumable ? "consumable" : item.slot || "consumable");
    return {
      ...draft,
      shop_type: shopType,
      item: { ...item, item_type: itemType, consumable: itemType === "consumable", slot: itemType === "consumable" ? null : itemType },
    };
  }
  return { ...draft, cooldown_seconds: 0 };
}

function QuestSummary({ row }) {
  return (
    <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr_1fr_1fr] gap-2 items-center">
      <div>
        <div className="font-semibold text-lg">{row.title || row.name || "Untitled quest"}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{row.slug}</div>
      </div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.quest_type} - Level {row.recommended_level || 1}+</div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.daily_free ? "Free once/day" : `${row.entry_cost || 0} ${currencyLabel({ id: row.currency_id || "free" })}`}</div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.event_quest ? (row.event_active ? "Limited-time visible" : "Limited-time hidden") : "Permanent"}</div>
    </div>
  );
}

function EventSummary({ row }) {
  return (
    <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr_1fr_1fr] gap-2 items-center">
      <div>
        <div className="font-semibold text-lg">{row.title || "Untitled event"}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">!{row.command_keyword}</div>
      </div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.event_type}</div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.duration_seconds || 60}s window</div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.enabled ? "Enabled" : "Disabled"}</div>
    </div>
  );
}

function ShopSummary({ row }) {
  const type = row.shop_type || "item";
  const detail = type === "loot_crate"
    ? "Rolls generated loot"
    : type === "material_pack"
      ? "Grants materials"
      : `${row.item?.item_type || row.item?.slot || (row.item?.consumable ? "consumable" : "item")} item`;
  return (
    <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr_1fr_1fr] gap-2 items-center">
      <div>
        <div className="font-semibold text-lg">{row.name || row.item?.name || "Shop item"}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">{row.description}</div>
      </div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{detail}</div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{tokenText(row.price || {})}</div>
      <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.enabled !== false ? "Enabled" : "Disabled"}</div>
    </div>
  );
}

function QuestForm({ value, onChange }) {
  const loot = value.loot_generation || {};
  const ai = value.ai_directives || {};
  const enc = value.encounter_generation || {};
  const rewards = value.reward_config || {};
  const setLoot = (patch) => onChange({ loot_generation: { ...loot, ...patch } });
  const setAi = (patch) => onChange({ ai_directives: { ...ai, ...patch } });
  const setRewards = (patch) => onChange({ reward_config: { ...rewards, ...patch } });
  return (
    <div className="space-y-6">
      <FormNote title="RPG Lite flow">
        Most streamers should only need setup, cost, loot basics, and rewards. Advanced controls are still here for custom event dungeons, special loot themes, and deeper balance tuning.
      </FormNote>

      <Section title="Quest Setup" help="The minimum information needed for viewers to understand and start the run.">
        <Input label="Quest name" help="Shown on the RPG page and chat list." value={value.title} onChange={title => onChange({ title })} />
        <Input label="Slug" help="One short word is best for viewers, such as cellar or burrow." value={value.slug} onChange={slug => onChange({ slug })} />
        <Select label="Quest type" help="The main fantasy/activity category for this run." value={value.quest_type} options={QUEST_TYPES} onChange={quest_type => onChange({ quest_type })} />
        <NumberInput label="Recommended level" help="Viewer-facing level guidance." value={value.recommended_level ?? 1} onChange={recommended_level => onChange({ recommended_level })} />
        <NumberInput label="Join timer seconds" help="When this timer ends, the quest starts with whoever joined." value={value.join_window_seconds ?? 60} onChange={join_window_seconds => onChange({ join_window_seconds })} />
        <NumberInput label="Quest duration seconds" help="Approximate run length after joining." value={value.duration_seconds ?? 90} onChange={duration_seconds => onChange({ duration_seconds })} />
        <Textarea label="Description" help="Plain explanation for the quest card." value={value.description} onChange={description => onChange({ description })} />
      </Section>

      <Section title="Cost and Visibility" help="Controls who can start the quest and whether it appears after a seasonal event ends.">
        <Toggle label="Free once per viewer per day" help="Use this for the daily very easy quest." checked={!!value.daily_free} onChange={daily_free => onChange({ daily_free })} />
        <NumberInput label="Entry cost" help="Cost to start the quest, or the cost after the daily free run has been used." value={value.entry_cost ?? 0} onChange={entry_cost => onChange({ entry_cost })} />
        <Select label="Normal currency" help="Main platform currency spent to start." value={value.currency_id || "free"} options={CURRENCIES} optionLabel={id => currencyLabel({ id })} onChange={currency_id => onChange({ currency_id })} />
        <Toggle label="Limit starter once per stream" help="If enabled, each viewer can start this quest once per stream. They can still join other runs." checked={!!value.limit_per_user_per_stream} onChange={limit_per_user_per_stream => onChange({ limit_per_user_per_stream })} />
        <Toggle label="Limited-time quest" help="Marks the quest as seasonal/event-only." checked={!!value.event_quest} onChange={event_quest => onChange({ event_quest })} />
        <Toggle label="Visible right now" help="Turn this off after the event ends. Loot already earned stays in inventories." checked={value.event_active !== false} onChange={event_active => onChange({ event_active })} />
      </Section>

      <Section title="Loot Basics" help="This is enough for most quests. Stat ranges and sell values stay global in Settings.">
        <Textarea label="Loot theme" help="Natural-language direction for generated item names and descriptions." value={ai.gear_brief} onChange={gear_brief => setAi({ gear_brief })} />
        <NumberInput label="Loot rolls per player" help="How many generated gear rolls each participant gets." value={loot.rolls ?? 1} onChange={rolls => onChange({ loot_generation: { ...loot, rolls }, loot_rolls: rolls })} />
        <CheckboxGroup label="Allowed item types" help="Only these generated item types can drop. Consumables are manual-use healing items." values={loot.slots || []} options={ITEM_TYPES} onChange={slots => onChange({ loot_generation: { ...loot, slots } })} />
        <Weights label="Rarity odds" help="Higher number means more common. Legendary can be tiny, such as 0.001." values={loot.rarity_weights || {}} keys={RARITIES} onChange={rarity_weights => setLoot({ rarity_weights })} />
      </Section>

      <Section title="Rewards" help="Rewards granted when the quest clears. Generated gear drops are controlled above.">
        <NumberInput label="XP per generated step" help="XP awarded for each encounter check." value={value.xp_per_encounter ?? 25} onChange={xp_per_encounter => onChange({ xp_per_encounter })} />
        <Weights label="Completion XP" help="Flat XP awarded at quest completion." values={rewards.xp || {}} keys={STATS} onChange={xp => setRewards({ xp })} />
        <CoinRanges label="Completion Dungeon Tokens" help="Flat Dungeon Tokens awarded after quest completion." values={rewards.wallet || {}} rangeMode={false} onChange={wallet => setRewards({ wallet })} />
        <KeyValue label="Materials" help="Material amounts granted at completion." values={rewards.materials || {}} onChange={materials => setRewards({ materials })} />
        <Toggle label="Enabled" help="Disabled quests do not appear." checked={value.enabled !== false} onChange={enabled => onChange({ enabled })} />
      </Section>

      <AdvancedSection title="Art and AI Direction" help="Use this when you want custom overlay art direction or more precise AI flavor.">
        <Textarea label="Visual reference" help="Used as generated art/theme direction, not gear rules." value={value.artwork_reference} onChange={artwork_reference => onChange({ artwork_reference })} />
        <ImageUploadField label="Backup dungeon image" help="Shown on the overlay if generated art is disabled or fails. Uploaded images are compressed to WebP." value={value.artwork_url || ""} onChange={artwork_url => onChange({ artwork_url })} />
        <Textarea label="Enemies and hazards" help="Describe the generated threats." value={ai.enemy_brief} onChange={enemy_brief => setAi({ enemy_brief })} />
        <Textarea label="Rooms and pacing" help="Describe quest beats and tone." value={ai.encounter_brief} onChange={encounter_brief => setAi({ encounter_brief })} />
      </AdvancedSection>

      <AdvancedSection title="Encounter Tuning" help="Use this when a quest should test specific skills or use a custom enemy/hazard vocabulary.">
        <Select label="Main generated threat" help="Enemy or hazard balance table to use." value={enc.kind || "enemy"} options={ENCOUNTER_KINDS} onChange={kind => onChange({ encounter_generation: { ...enc, kind } })} />
        <NumberInput label="Number of steps" help="How many generated checks happen during the quest." value={value.encounter_count ?? 4} onChange={encounter_count => onChange({ encounter_count, encounter_tiers: Array.from({ length: encounter_count }, (_, i) => (value.encounter_tiers || [])[i] || 1) })} />
        <TextList label="Threat prefixes" help="First part of generated enemy/hazard names." values={enc.enemy_prefixes || []} onChange={enemy_prefixes => onChange({ encounter_generation: { ...enc, enemy_prefixes } })} />
        <TextList label="Threat nouns" help="Second part of generated enemy/hazard names." values={enc.enemy_nouns || []} onChange={enemy_nouns => onChange({ encounter_generation: { ...enc, enemy_nouns } })} />
        <Weights label="Skill odds" help="Which skills this quest tends to test." values={enc.skill_weights || {}} keys={STATS} onChange={skill_weights => onChange({ encounter_generation: { ...enc, skill_weights } })} />
      </AdvancedSection>

      <AdvancedSection title="Detailed Item Naming" help="Most quests can use the loot theme above. Use these controls for exact AI prompts and fallback name parts.">
        <NamePromptFields loot={loot} onChange={loot_generation => onChange({ loot_generation })} enabledTypes={loot.slots || ITEM_TYPES} />
        <NamingRulesEditor loot={loot} onChange={loot_generation => onChange({ loot_generation })} enabledTypes={loot.slots || ITEM_TYPES} />
      </AdvancedSection>
    </div>
  );
}

function EventForm({ value, onChange }) {
  const ai = value.ai_directives || {};
  const gen = value.reward_generation || {};
  const setGen = (patch) => onChange({ reward_generation: { ...gen, ...patch } });
  return (
    <div className="space-y-6">
      <FormNote title="Events are not dungeons">
        Events are lightweight chat-wide popups. They rotate on the Settings cadence, accept everyone who uses the command while live, and then grant small rewards.
      </FormNote>

      <Section title="Event Setup" help="Random events appear in chat and accept everyone who types the command during the window.">
        <Input label="Event name" help="Internal/display name." value={value.title} onChange={title => onChange({ title })} />
        <Select label="Type" help="The kind of random popup this is." value={value.event_type} options={EVENT_TYPES} onChange={event_type => onChange({ event_type })} />
        <Input label="Command" help="Chat command without !. Example: gather" value={value.command_keyword} onChange={command_keyword => onChange({ command_keyword })} />
        <NumberInput label="Join window seconds" help="How long viewers can join after the event appears." value={value.duration_seconds ?? 60} onChange={duration_seconds => onChange({ duration_seconds })} />
        <Textarea label="Fallback chat message" help="Used if AI is disabled or capped. Include the command." value={value.announcement_text} onChange={announcement_text => onChange({ announcement_text })} />
        <Toggle label="Enabled" help="Only enabled events can be randomly rotated." checked={value.enabled !== false} onChange={enabled => onChange({ enabled })} />
      </Section>

      <Section title="Rewards" help="Each participant rolls from these ranges. No per-event cooldown is needed because settings rotate events.">
        <Weights label="Skill XP odds" help="Which skill gets XP for each participant." values={gen.skill_weights || {}} keys={STATS} onChange={skill_weights => setGen({ skill_weights })} />
        <RangeInput label="XP range" help="XP amount awarded to the chosen skill." value={gen.xp || [4, 10]} onChange={xp => setGen({ xp })} />
        <CoinRanges label="Dungeon Token range" help="Dungeon Tokens rolled per participant." values={gen.wallet || {}} onChange={wallet => setGen({ wallet })} />
        <KeyRange label="Material ranges" help="Material amounts rolled per participant." values={gen.materials || {}} onChange={materials => setGen({ materials })} />
      </Section>

      <AdvancedSection title="AI Event Flavor" help="Optional one-sentence announcement and reward style direction.">
        <Textarea label="Event flavor" help="What appears in chat." value={ai.event_brief} onChange={event_brief => onChange({ ai_directives: { ...ai, event_brief } })} />
        <Textarea label="Reward flavor" help="What rewards should feel like." value={ai.reward_brief} onChange={reward_brief => onChange({ ai_directives: { ...ai, reward_brief } })} />
      </AdvancedSection>
    </div>
  );
}

function ShopForm({ value, onChange }) {
  const item = value.item || {};
  const stats = item.stats || {};
  const loot = value.loot_generation || {};
  const ai = value.ai_directives || {};
  const shopType = value.shop_type || "item";
  const itemType = item.item_type || item.slot || (item.consumable ? "consumable" : "consumable");
  const setItemType = (nextType) => onChange({
    item: {
      ...item,
      item_type: nextType,
      consumable: nextType === "consumable",
      slot: nextType === "consumable" ? null : nextType,
    },
  });
  return (
    <div className="space-y-6">
      <FormNote title="Shop Lite flow">
        Most shops only need a listing, a type, a price, and either a designed item, loot crate style, or material pack. Use advanced crate naming only when the fallback names need exact control.
      </FormNote>

      <Section title="Shop Listing" help="This is what viewers see in the RPG shop.">
        <Input label="Shop item name" help="Displayed in the shop." value={value.name} onChange={name => onChange({ name, item: { ...item, name } })} />
        <Textarea label="Description" help="Short reason to buy it." value={value.description} onChange={description => onChange({ description })} />
        <Select label="Shop type" help="Item gives a designed item, loot crate rolls generated loot, material pack grants materials." value={shopType} options={SHOP_TYPES} onChange={shop_type => onChange({ shop_type })} />
        <ImageUploadField label="Shop listing image" help="Shown on the shop card. Use this for loot crate and material pack images too." value={value.image_url || ""} onChange={image_url => onChange({ image_url })} />
        <Toggle label="Enabled" help="Disabled shop items are hidden from viewers." checked={value.enabled !== false} onChange={enabled => onChange({ enabled })} />
      </Section>
      {shopType === "item" && (
        <>
          <Section title="Designed Item" help="Use this for potions, fixed weapons, armor, trinkets, or other intentional shop rewards.">
            <Select label="Item type" help="Consumables can be used; gear can be equipped in its slot." value={itemType} options={ITEM_TYPES} onChange={setItemType} />
            <Select label="Rarity" help="Controls display color in inventory." value={item.rarity || "common"} options={RARITIES} onChange={rarity => onChange({ item: { ...item, rarity } })} />
            <ImageUploadField label="Inventory item image" help="Shown after purchase. If blank, the shop listing image can still show in the shop." value={item.image_url || ""} onChange={image_url => onChange({ item: { ...item, image_url } })} />
            <Textarea label="Inventory flavor" help="Text shown on the item after purchase." value={item.flavor || ""} onChange={flavor => onChange({ item: { ...item, flavor } })} />
            <KeyValue label="Stats" help="Format: attack:2, defense:1, heal:60. Heal only matters on consumables." values={stats} onChange={nextStats => onChange({ item: { ...item, stats: nextStats } })} />
            <CoinRanges label="Sell value" help="Optional Dungeon Tokens returned if the viewer sells this item." values={item.sell_value || {}} rangeMode={false} onChange={sell_value => onChange({ item: { ...item, sell_value } })} />
          </Section>
        </>
      )}
      {shopType === "loot_crate" && (
        <Section title="Loot Crate Rules" help="Crates roll one generated item using these odds and item types. Stat ranges still come from global Settings.">
          <Textarea label="Crate loot style" help="Flavor stored on generated items from this crate." value={ai.gear_brief || ""} onChange={gear_brief => onChange({ ai_directives: { ...ai, gear_brief } })} />
          <CheckboxGroup label="Possible item types" help="Generated result can be any selected type." values={loot.slots || []} options={ITEM_TYPES} onChange={slots => onChange({ loot_generation: { ...loot, slots } })} />
          <Weights label="Rarity odds" help="Supports decimals. Example: legendary 0.001." values={loot.rarity_weights || {}} keys={RARITIES} onChange={rarity_weights => onChange({ loot_generation: { ...loot, rarity_weights } })} />
        </Section>
      )}
      {shopType === "loot_crate" && (
        <AdvancedSection title="Loot Crate Naming" help="Optional AI prompts and fallback naming parts for crate rewards.">
          <NamePromptFields loot={loot} onChange={loot_generation => onChange({ loot_generation })} enabledTypes={loot.slots || ITEM_TYPES} />
          <NamingRulesEditor loot={loot} onChange={loot_generation => onChange({ loot_generation })} enabledTypes={loot.slots || ITEM_TYPES} />
        </AdvancedSection>
      )}
      {shopType === "material_pack" && (
        <Section title="Material Pack" help="Grants these materials immediately after purchase. Useful for future crafting sinks or event cleanup.">
          <KeyValue label="Materials granted" help="Format: scrap:5, loose_parts:2." values={value.materials || {}} onChange={materials => onChange({ materials })} />
        </Section>
      )}
      <Section title="Price" help="Dungeon Token cost. Bigger potions should be better HP per token, but require saving more.">
        <CoinRanges label="Price" help="Exact Dungeon Tokens charged on purchase." values={value.price || {}} rangeMode={false} onChange={price => onChange({ price })} />
      </Section>
    </div>
  );
}

function NamingRulesEditor({ loot, onChange, enabledTypes }) {
  const types = (enabledTypes || ITEM_TYPES).filter(type => ITEM_TYPES.includes(type));
  return (
    <div className="surface-2 p-4 col-span-full">
      <div className="font-semibold mb-1">Structured fallback names</div>
      <p className="text-xs leading-5 text-[hsl(var(--muted-foreground))] mb-4">
        These are only needed when you want exact naming control. Gear names use prefix + material + noun. Consumables use prefix + noun.
      </p>
      <div className="space-y-5">
        {types.map(type => (
          <div key={type} className="border border-[hsl(var(--border))] rounded p-3">
            <div className="font-semibold capitalize mb-3">{type}</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {RARITIES.map(rarity => {
                const parts = ((loot.name_parts_by_slot_rarity || {})[type] || {})[rarity] || {};
                const updateParts = (patch) => onChange({
                  ...loot,
                  name_parts_by_slot_rarity: {
                    ...(loot.name_parts_by_slot_rarity || {}),
                    [type]: {
                      ...((loot.name_parts_by_slot_rarity || {})[type] || {}),
                      [rarity]: { ...parts, ...patch },
                    },
                  },
                });
                return (
                  <div key={`${type}-${rarity}`} className="surface-2 p-3">
                    <div className="label-eyebrow mb-2">{rarity}</div>
                    <TextList label="Prefixes" help="First word choices for this type and rarity." values={parts.prefixes || []} onChange={prefixes => updateParts({ prefixes })} />
                    {type !== "consumable" && <TextList label="Materials" help="Middle word choices for this type and rarity." values={parts.materials || []} onChange={materials => updateParts({ materials })} />}
                    <TextList label="Nouns" help={type === "consumable" ? "Consumable endings. Names use prefix + noun only." : "Name endings for this type and rarity."} values={parts.nouns || (loot.nouns || {})[type] || []} onChange={nouns => updateParts({ nouns })} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NamePromptFields({ loot, onChange, enabledTypes }) {
  const prompts = loot.name_prompts_by_type || {};
  const types = (enabledTypes || ITEM_TYPES).filter(type => ITEM_TYPES.includes(type));
  const updatePrompt = (type, prompt) => onChange({
    ...loot,
    name_prompts_by_type: {
      ...DEFAULT_NAME_PROMPTS,
      ...prompts,
      [type]: prompt,
    },
  });
  return (
    <div className="surface-2 p-4 col-span-full">
      <div className="font-semibold mb-1">AI Naming Prompts</div>
      <p className="text-xs leading-5 text-[hsl(var(--muted-foreground))] mb-4">
        These guide OpenAI item names for each enabled type. If AI is disabled or capped, structured naming rules still work as fallback.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {types.map(type => (
          <Textarea
            key={type}
            label={`${type} naming prompt`}
            help="Short direction for generated item names of this type."
            value={prompts[type] ?? DEFAULT_NAME_PROMPTS[type] ?? ""}
            onChange={value => updatePrompt(type, value)}
          />
        ))}
      </div>
    </div>
  );
}

function RPGOverlaySettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/rpg-settings").then(r => setS(r.data)); }, []);
  if (!s) return <div className="text-sm">Loading...</div>;
  const save = async () => {
    await api.patch("/admin/rpg-settings", s);
    toast.success("Overlay settings saved");
  };
  const patch = (p) => setS({ ...s, ...p });
  return (
    <div className="space-y-6 max-w-7xl">
      <OverlayDisplaySection overlay={s.rpg_overlay_display || {}} patch={patch} />
      <button onClick={save} className="bg-[hsl(var(--primary))] text-white text-sm px-5 py-3 rounded flex items-center gap-2"><Save className="w-4 h-4" /> Save overlay</button>
    </div>
  );
}

function OverlayDisplaySection({ overlay, patch }) {
  return (
    <Section title="Dungeon Overlay Popup" help="Controls the on-stream dungeon card. Default layout is top right: generated name, image, progress, then roll text.">
      <Toggle label="Show popup" help="Turns the dungeon progress panel on or off for the stream overlay." checked={overlay.enabled !== false} onChange={enabled => patch({ rpg_overlay_display: { ...overlay, enabled } })} />
      <Select label="Position" help="Which corner of the overlay the popup anchors to." value={overlay.position || "top_right"} options={["top_right", "top_left", "bottom_right", "bottom_left"]} onChange={position => patch({ rpg_overlay_display: { ...overlay, position } })} />
      <NumberInput label="Width percent" help="Popup width as a percent of the overlay width." value={overlay.width_vw ?? 38} step="0.1" onChange={width_vw => patch({ rpg_overlay_display: { ...overlay, width_vw } })} />
      <NumberInput label="Minimum width" help="Smallest popup width in pixels." value={overlay.min_width_px ?? 520} onChange={min_width_px => patch({ rpg_overlay_display: { ...overlay, min_width_px } })} />
      <NumberInput label="Maximum width" help="Largest popup width in pixels." value={overlay.max_width_px ?? 720} onChange={max_width_px => patch({ rpg_overlay_display: { ...overlay, max_width_px } })} />
      <NumberInput label="Bottom offset" help="Distance from the bottom edge as a percent of overlay height." value={overlay.bottom_vh ?? 5.4} step="0.1" onChange={bottom_vh => patch({ rpg_overlay_display: { ...overlay, bottom_vh } })} />
      <NumberInput label="Top offset" help="Distance from the top edge as a percent of overlay height." value={overlay.top_vh ?? 4} step="0.1" onChange={top_vh => patch({ rpg_overlay_display: { ...overlay, top_vh } })} />
      <NumberInput label="Left offset" help="Distance from the left edge as a percent of overlay width." value={overlay.left_vw ?? 2.2} step="0.1" onChange={left_vw => patch({ rpg_overlay_display: { ...overlay, left_vw } })} />
      <NumberInput label="Right offset" help="Distance from the right edge as a percent of overlay width." value={overlay.right_vw ?? 3} step="0.1" onChange={right_vw => patch({ rpg_overlay_display: { ...overlay, right_vw } })} />
      <Toggle label="Show dungeon image" help="Shows generated dungeon art at the top of the popup when art is available. If generation fails, it uses the backup image." checked={overlay.image_enabled !== false} onChange={image_enabled => patch({ rpg_overlay_display: { ...overlay, image_enabled } })} />
      <NumberInput label="Image height" help="Height of the dungeon image area in pixels." value={overlay.image_height_px ?? 260} onChange={image_height_px => patch({ rpg_overlay_display: { ...overlay, image_height_px } })} />
      <NumberInput label="Image opacity" help="1 is fully visible. Lower values blend it back." value={overlay.image_opacity ?? 0.92} step="0.05" onChange={image_opacity => patch({ rpg_overlay_display: { ...overlay, image_opacity } })} />
      <ImageUploadField label="Global backup image" help="Used if a quest has no backup image and generated art fails or is disabled." value={overlay.fallback_image_url || ""} onChange={fallback_image_url => patch({ rpg_overlay_display: { ...overlay, fallback_image_url } })} />
    </Section>
  );
}

function RPGSettings() {
  const [s, setS] = useState(null);
  const [ttsText, setTtsText] = useState("The barracks quest board hums to life.");
  const [artBackfillBusy, setArtBackfillBusy] = useState(false);
  const [artMissing, setArtMissing] = useState(null);
  useEffect(() => { api.get("/admin/rpg-settings").then(r => setS(r.data)); }, []);
  useEffect(() => {
    api.get("/admin/rpg/items/missing-art-count")
      .then(r => setArtMissing(r.data))
      .catch(() => setArtMissing(null));
  }, []);
  if (!s) return <div className="text-sm">Loading...</div>;
  const save = async () => {
    await api.patch("/admin/rpg-settings", s);
    toast.success("Saved");
  };
  const patch = (p) => setS({ ...s, ...p });
  const balance = s.generated_balance_rules || {};
  const gear = balance.gear || {};
  const encounters = balance.encounters || {};
  const sell = s.generated_item_rarity_rules || {};
  const overlay = s.rpg_overlay_display || {};
  const testTts = async () => {
    try {
      const { data } = await api.post("/admin/tts/test", { text: ttsText });
      new Audio(mediaUrl(data.audio_url)).play();
      toast.success("Playing TTS preview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "TTS test failed. Check ELEVENLABS_API_KEY and ELEVENLABS_VOICE_ID.");
    }
  };
  const backfillItemArt = async () => {
    setArtBackfillBusy(true);
    try {
      const { data } = await api.post("/admin/rpg/items/backfill-art", { limit: 5 });
      toast.success(`Item art: ${data.generated} generated, ${data.skipped} skipped, ${data.failed} failed`);
      const count = await api.get("/admin/rpg/items/missing-art-count");
      setArtMissing(count.data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.message || e.message;
      const status = e.response?.status ? ` (${e.response.status})` : "";
      toast.error(`Item art backfill failed${status}: ${detail || "unknown error"}`);
    } finally {
      setArtBackfillBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl">
      <Section title="Progression" help="Controls how slowly viewers level and how sturdy new characters are.">
        <NumberInput label="XP curve base" help="Higher means slower early leveling." value={s.xp_curve_base ?? 83} onChange={xp_curve_base => patch({ xp_curve_base })} />
        <NumberInput label="XP curve exponent" help="Higher means later levels take much longer." value={s.xp_curve_exponent ?? 2} step="0.1" onChange={xp_curve_exponent => patch({ xp_curve_exponent })} />
        <NumberInput label="Starting HP" help="New/reset RPG characters start here." value={s.starting_hp ?? 100} onChange={starting_hp => patch({ starting_hp })} />
        <NumberInput label="HP per level" help="Future growth value for RPG balancing." value={s.level_hp_per_level ?? 5} onChange={level_hp_per_level => patch({ level_hp_per_level })} />
        <NumberInput label="Max inventory slots" help="Maximum separate inventory rows per viewer. Stacked potions count as one slot." value={s.max_inventory_slots ?? 250} onChange={max_inventory_slots => patch({ max_inventory_slots })} />
      </Section>

      <Section title="Simple Quest Command" help="Main viewer path: !quest shows help, !quest free starts the daily quest, and !quest bunny500 or !quest rat100 spends currency to scale a generated quest. Bunny Tokens are the larger free-currency spend; Rat Coins are the smaller paid-currency spend. Minimums and cooldowns help prevent spam.">
        <Toggle label="Enable generated quests" help="Turns on the simple !quest free, !quest bunny, and !quest rat flow." checked={s.generated_quests_enabled !== false} onChange={generated_quests_enabled => patch({ generated_quests_enabled })} />
        <Toggle label="Daily free quest" help="Each viewer can run one free generated quest per day." checked={s.generated_quest_free_daily_enabled !== false} onChange={generated_quest_free_daily_enabled => patch({ generated_quest_free_daily_enabled })} />
        <NumberInput label="Join window seconds" help="How long chat has to type !join before the generated quest auto-starts." value={s.generated_quest_join_window_seconds ?? 45} onChange={generated_quest_join_window_seconds => patch({ generated_quest_join_window_seconds })} />
        <NumberInput label="Bunny token minimum" help="Lowest Bunny Token amount accepted, such as !quest bunny100." value={s.generated_quest_bunny_min ?? 100} onChange={generated_quest_bunny_min => patch({ generated_quest_bunny_min })} />
        <NumberInput label="Bunny token maximum" help="Highest Bunny Token amount accepted. Larger amounts should not explode rewards." value={s.generated_quest_bunny_max ?? 10000} onChange={generated_quest_bunny_max => patch({ generated_quest_bunny_max })} />
        <NumberInput label="Rat Coin minimum" help="Lowest Rat Coin amount accepted, such as !quest rat25." value={s.generated_quest_rat_min ?? 25} onChange={generated_quest_rat_min => patch({ generated_quest_rat_min })} />
        <NumberInput label="Rat Coin maximum" help="Highest Rat Coin amount accepted. Rat Coin quests should need fewer coins than Bunny Token quests." value={s.generated_quest_rat_max ?? 5000} onChange={generated_quest_rat_max => patch({ generated_quest_rat_max })} />
        <NumberInput label="Global start cooldown" help="Seconds after a quest starts or completes before anyone can start another. Active runs are already blocked." value={s.dungeon_global_cooldown_seconds ?? 120} onChange={dungeon_global_cooldown_seconds => patch({ dungeon_global_cooldown_seconds })} />
        <NumberInput label="Viewer start cooldown" help="Seconds before the same viewer can start another quest. They can still join someone else's party." value={s.dungeon_viewer_cooldown_seconds ?? 300} onChange={dungeon_viewer_cooldown_seconds => patch({ dungeon_viewer_cooldown_seconds })} />
        <Textarea label="Generated quest theme" help="Short style guide for generated quests, enemies, and loot. Keep it broad so runs feel varied." value={s.generated_quest_theme || ""} onChange={generated_quest_theme => patch({ generated_quest_theme })} />
        <Toggle label="Generate dungeon images" help="Optional and off by default to keep image costs low. Existing special quest artwork is unaffected." checked={!!s.generated_quest_dungeon_images_enabled} onChange={generated_quest_dungeon_images_enabled => patch({ generated_quest_dungeon_images_enabled })} />
        <Toggle label="Generate item images" help="Optional and off by default. Generated quest loot will otherwise use the clean fallback card display." checked={!!s.generated_quest_item_images_enabled} onChange={generated_quest_item_images_enabled => patch({ generated_quest_item_images_enabled })} />
      </Section>

      <Section title="AI and Narration" help="AI text is always short. TTS uses the shared ElevenLabs setup.">
        <Toggle label="Overlay narration" help="Queue generated RPG text to the overlay." checked={!!s.narrate_enabled} onChange={narrate_enabled => patch({ narrate_enabled })} />
        <Toggle label="AI narration" help="Generate one-sentence intro, loot, event, and completion lines." checked={!!s.ai_narration_enabled} onChange={ai_narration_enabled => patch({ ai_narration_enabled })} />
        <NumberInput label="AI chance" help="1 means always try AI until the cap is hit." value={s.ai_narration_chance ?? 1} step="0.05" onChange={ai_narration_chance => patch({ ai_narration_chance })} />
        <NumberInput label="AI max characters" help="Hard cap for each RPG narration line." value={s.ai_narration_max_chars ?? 160} onChange={ai_narration_max_chars => patch({ ai_narration_max_chars })} />
        <NumberInput label="AI narration daily cap" help="Maximum RPG narration calls per day." value={s.ai_narration_max_per_day ?? 80} onChange={ai_narration_max_per_day => patch({ ai_narration_max_per_day })} />
        <Toggle label="AI quest naming" help="Generate a short quest name/giver when a run starts." checked={!!s.ai_quest_generation_enabled} onChange={ai_quest_generation_enabled => patch({ ai_quest_generation_enabled })} />
        <NumberInput label="Quest naming daily cap" help="Maximum AI quest-start calls per day." value={s.ai_quest_generation_max_per_day ?? 25} onChange={ai_quest_generation_max_per_day => patch({ ai_quest_generation_max_per_day })} />
        <Textarea label="Intro fallback" help="Used when AI is disabled or capped." value={s.narrate_intro || ""} onChange={narrate_intro => patch({ narrate_intro })} />
        <Textarea label="Loot fallback" help="Used when AI is disabled or capped." value={s.narrate_loot || ""} onChange={narrate_loot => patch({ narrate_loot })} />
        <Textarea label="Completion fallback" help="Used when AI is disabled or capped." value={s.narrate_complete || ""} onChange={narrate_complete => patch({ narrate_complete })} />
      </Section>

      <Section title="Generated Item Images" help="Creates images for old generated inventory items and shop products that do not have art yet. Runs in small batches so image costs stay controlled.">
        <div className="surface-2 p-4 col-span-full flex flex-col md:flex-row md:items-center gap-4">
          <div className="text-sm leading-6 text-[hsl(var(--muted-foreground))] flex-1">
            Backfill up to 5 existing generated loot or shop items without images. Existing item stats, names, rarity, and ownership stay unchanged.
            <div className="mt-2 font-mono text-[#f3ead3]">
              {artMissing
                ? `${artMissing.missing} missing images (${artMissing.inventory_missing || 0} inventory, ${artMissing.shop_missing || 0} shop) across ${artMissing.viewers_with_missing} viewer inventories`
                : "Checking missing image count..."}
            </div>
          </div>
          <button
            type="button"
            disabled={artBackfillBusy}
            onClick={backfillItemArt}
            className="bg-[hsl(var(--primary))] text-white text-sm px-5 py-3 rounded flex items-center gap-2 disabled:opacity-60"
          >
            <Sparkles className="w-4 h-4" /> {artBackfillBusy ? "Generating..." : "Generate missing item art"}
          </button>
        </div>
      </Section>

      <Section title="Random Events" help="Events rotate from the Events tab. No per-event cooldowns are needed.">
        <Toggle label="Random events enabled" help="When on, chat activity can trigger eligible events." checked={!!s.random_events_enabled} onChange={random_events_enabled => patch({ random_events_enabled })} />
        <NumberInput label="Check every X minutes" help="How often the system is allowed to roll for an event." value={s.random_event_interval_minutes ?? 30} onChange={random_event_interval_minutes => patch({ random_event_interval_minutes })} />
        <NumberInput label="Chance per check" help="0.35 means 35 percent chance each interval." value={s.random_event_chance ?? 0.35} step="0.05" onChange={random_event_chance => patch({ random_event_chance })} />
      </Section>

      <AdvancedPanel title="Balance and Economy" help="Streamer-safe defaults should work for most channels. Open this only when tuning stat ranges, sell values, consumable healing, or enemy difficulty.">
        <Section title="Generated Gear Balance" help="Global stat ranges by slot and rarity. This is what makes rare gear actually better.">
          {GEAR_SLOTS.map(slot => (
            <div key={slot} className="surface-2 p-4 col-span-full">
              <div className="font-semibold capitalize mb-3">{slot}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                {RARITIES.map(rarity => <BalanceCard key={rarity} label={rarity} value={(gear[slot] || {})[rarity] || { stat_count: [1, 1], stat_min: 1, stat_max: 1 }} onChange={rule => patch({ generated_balance_rules: { ...balance, gear: { ...gear, [slot]: { ...(gear[slot] || {}), [rarity]: rule } }, encounters } })} />)}
              </div>
            </div>
          ))}
        </Section>

        <Section title="Sell Values" help="Generated gear sells for Dungeon Tokens. Keep common low so rare drops feel special.">
          {RARITIES.map(rarity => <CoinRanges key={rarity} label={`${rarity} sell value`} help="Dungeon Token range when this rarity is sold." values={(sell[rarity] || {}).sell_value || {}} onChange={sell_value => patch({ generated_item_rarity_rules: { ...sell, [rarity]: { ...(sell[rarity] || {}), sell_value } } })} />)}
        </Section>

        <Section title="Material Sell Values" help="Fallback value for gathered materials, including old materials from deleted dungeons. Use default for anything not listed.">
          {Object.entries(s.material_sell_values || { default: { [DUNGEON_TOKEN_KEY]: 1 } }).map(([material, value]) => (
            <CoinRanges
              key={material}
              label={material}
              help="Exact Dungeon Tokens paid per one material sold."
              values={value || {}}
              rangeMode={false}
              onChange={nextValue => patch({ material_sell_values: { ...(s.material_sell_values || {}), [material]: nextValue } })}
            />
          ))}
          <KeyValue
            label="Add materials"
            help="Add or overwrite material sell values as Dungeon Token amounts. Format: frost_shard:3, event_token:10."
            values={{}}
            onChange={entries => {
              const next = { ...(s.material_sell_values || {}) };
              Object.entries(entries || {}).forEach(([material, tokens]) => { next[material] = { [DUNGEON_TOKEN_KEY]: tokens }; });
              patch({ material_sell_values: next });
            }}
          />
        </Section>

        <Section title="Consumable Healing" help="Generated consumables roll healing from these global rarity ranges. They are manual-use items, not equipment.">
          {RARITIES.map(rarity => (
            <RangeInput
              key={rarity}
              label={`${rarity} healing`}
              help="Minimum and maximum HP restored by generated consumables of this rarity."
              value={(s.generated_consumable_healing || {})[rarity] || DEFAULT_CONSUMABLE_HEALING[rarity]}
              onChange={range => patch({ generated_consumable_healing: { ...(s.generated_consumable_healing || {}), [rarity]: range } })}
            />
          ))}
        </Section>

        <Section title="Enemy and Hazard Balance" help="Global DC and damage ranges by tier. Quests choose enemy or hazard and skill odds.">
          {ENCOUNTER_KINDS.map(kind => (
            <div key={kind} className="surface-2 p-4 col-span-full">
              <div className="font-semibold capitalize mb-3">{kind}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                {TIERS.map(tier => <EncounterBalance key={tier} label={`Tier ${tier}`} value={(encounters[kind] || {})[tier] || { dc: [5, 8], damage: [0, 5] }} onChange={rule => patch({ generated_balance_rules: { ...balance, gear, encounters: { ...encounters, [kind]: { ...(encounters[kind] || {}), [tier]: rule } } } })} />)}
              </div>
            </div>
          ))}
        </Section>
      </AdvancedPanel>

      <AdvancedPanel title="Diagnostics" help="Testing tools for setup troubleshooting. Most streamers do not need these during normal RPG setup.">
        <Section title="TTS Test" help="Uses the shared ElevenLabs environment: ELEVENLABS_API_KEY and ELEVENLABS_VOICE_ID.">
          <div className="col-span-full flex gap-2">
            <input value={ttsText} onChange={e => setTtsText(e.target.value)} className={inputClass} />
            <button onClick={testTts} className="text-xs uppercase tracking-widest border border-[hsl(var(--border))] px-4 py-2 rounded hover:border-[hsl(var(--primary))]">Speak</button>
          </div>
        </Section>
      </AdvancedPanel>

      <button onClick={save} className="bg-[hsl(var(--primary))] text-white text-sm px-5 py-3 rounded flex items-center gap-2"><Save className="w-4 h-4" /> Save settings</button>
    </div>
  );
}

function Section({ title, help, children }) {
  return (
    <div className="surface p-5">
      <div className="text-xl font-semibold">{title}</div>
      <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 leading-6">{help}</div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">{children}</div>
    </div>
  );
}

function AdvancedSection({ title, help, children, defaultOpen = false }) {
  return (
    <details className="surface overflow-hidden group" open={defaultOpen}>
      <summary className="list-none cursor-pointer p-5 flex items-start justify-between gap-4 hover:bg-[hsl(var(--surface-2))]">
        <div>
          <div className="label-eyebrow text-[hsl(var(--primary))]">Advanced</div>
          <div className="text-xl font-semibold mt-1">{title}</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 leading-6">{help}</div>
        </div>
        <ChevronDown className="w-5 h-5 mt-1 text-[hsl(var(--muted-foreground))]" />
      </summary>
      <div className="border-t border-[hsl(var(--border))] p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">{children}</div>
      </div>
    </details>
  );
}

function AdvancedPanel({ title, help, children, defaultOpen = false }) {
  return (
    <details className="space-y-4" open={defaultOpen}>
      <summary className="surface list-none cursor-pointer p-5 flex items-start justify-between gap-4 hover:bg-[hsl(var(--surface-2))]">
        <div>
          <div className="label-eyebrow text-[hsl(var(--primary))]">Advanced</div>
          <div className="text-xl font-semibold mt-1">{title}</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 leading-6">{help}</div>
        </div>
        <ChevronDown className="w-5 h-5 mt-1 text-[hsl(var(--muted-foreground))]" />
      </summary>
      <div className="space-y-6">{children}</div>
    </details>
  );
}

function FormNote({ title, children }) {
  return (
    <div className="surface-2 p-4 col-span-full">
      <div className="font-semibold">{title}</div>
      <div className="text-sm leading-6 text-[hsl(var(--muted-foreground))] mt-1">{children}</div>
    </div>
  );
}

const inputClass = "w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm";

function FieldWrap({ label = "", help = "", children } = {}) {
  return <label className="block"><div className="label-eyebrow mb-1.5">{label}</div>{children}<div className="mt-1 text-xs leading-5 text-[hsl(var(--muted-foreground))]">{help}</div></label>;
}
function Input({ label = "", help = "", value, onChange } = {}) {
  return <FieldWrap label={label} help={help}><input value={value || ""} onChange={e => onChange(e.target.value)} className={inputClass} /></FieldWrap>;
}
function Textarea({ label = "", help = "", value, onChange } = {}) {
  return <FieldWrap label={label} help={help}><textarea rows={3} value={value || ""} onChange={e => onChange(e.target.value)} className={inputClass} /></FieldWrap>;
}
function NumberInput({ label = "", help = "", value, onChange, step = "1" } = {}) {
  return <FieldWrap label={label} help={help}><input type="number" step={step} value={value ?? 0} onChange={e => onChange(step === "1" ? parseInt(e.target.value || 0) : parseFloat(e.target.value || 0))} className={inputClass} /></FieldWrap>;
}
function Select({ label = "", help = "", value, options, onChange, optionLabel } = {}) {
  return <FieldWrap label={label} help={help}><select value={value || options[0]} onChange={e => onChange(e.target.value)} className={inputClass}>{options.map(o => <option key={o} value={o}>{optionLabel ? optionLabel(o) : o}</option>)}</select></FieldWrap>;
}
function Toggle({ label = "", help = "", checked, onChange } = {}) {
  return <FieldWrap label={label} help={help}><input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="h-5 w-5" /></FieldWrap>;
}
function ImageUploadField({ label = "", help = "", value, onChange } = {}) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/rpg/art/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.image_url);
      toast.success("Image uploaded and compressed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <FieldWrap label={label} help={help}>
      <div className="space-y-3">
        {value && <img src={mediaUrl(value)} alt="" className="h-28 w-full rounded object-cover border border-[hsl(var(--border))]" />}
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[hsl(var(--border))] px-4 py-2 rounded hover:border-[hsl(var(--primary))] cursor-pointer">
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading..." : value ? "Replace image" : "Upload image"}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={uploading} onChange={e => upload(e.target.files?.[0])} />
          </label>
          {value && (
            <button type="button" onClick={() => onChange("")} className="text-xs uppercase tracking-widest border border-[hsl(var(--border))] px-4 py-2 rounded text-[hsl(var(--muted-foreground))] hover:text-white">
              Clear
            </button>
          )}
        </div>
      </div>
    </FieldWrap>
  );
}
function CheckboxGroup({ label = "", help = "", values, options, onChange } = {}) {
  const set = new Set(values || []);
  return <FieldWrap label={label} help={help}><div className="flex flex-wrap gap-3">{options.map(o => <label key={o} className="text-sm flex items-center gap-2"><input type="checkbox" checked={set.has(o)} onChange={e => { const next = new Set(set); e.target.checked ? next.add(o) : next.delete(o); onChange([...next]); }} />{o}</label>)}</div></FieldWrap>;
}
function TextList({ label = "", help = "", values, onChange } = {}) {
  return <FieldWrap label={label} help={help}><input value={(values || []).join(", ")} onChange={e => onChange(e.target.value.split(",").map(v => v.trim()).filter(Boolean))} className={inputClass} /></FieldWrap>;
}
function Weights({ label = "", help = "", values, keys, onChange } = {}) {
  return (
    <FieldWrap label={label} help={help}>
      <div className="grid grid-cols-1 gap-2">
        {keys.map(k => (
          <label key={k} className="grid grid-cols-[120px_1fr] items-center gap-2 text-sm">
            <span className="capitalize text-[hsl(var(--muted-foreground))]">{k.replaceAll("_", " ")}</span>
            <input type="number" step="0.001" value={values?.[k] ?? 0} onChange={e => onChange({ ...(values || {}), [k]: parseFloat(e.target.value || 0) })} className={inputClass} />
          </label>
        ))}
      </div>
    </FieldWrap>
  );
}
function RangeInput({ label = "", help = "", value, onChange } = {}) {
  const v = Array.isArray(value) ? value : [0, 0];
  return (
    <FieldWrap label={label} help={help}>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" aria-label={`${label} minimum`} title="Minimum" value={v[0] ?? 0} onChange={e => onChange([parseInt(e.target.value || 0), v[1] ?? 0])} className={inputClass} />
        <input type="number" aria-label={`${label} maximum`} title="Maximum" value={v[1] ?? 0} onChange={e => onChange([v[0] ?? 0, parseInt(e.target.value || 0)])} className={inputClass} />
      </div>
      <div className="mt-1 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
        <span>Minimum</span>
        <span>Maximum</span>
      </div>
    </FieldWrap>
  );
}
function CoinRanges({ label = "", help = "", values, onChange, rangeMode = true } = {}) {
  const currentRange = tokenRangeValue(values || {});
  const currentAmount = tokenValue(values || {});
  return (
    <FieldWrap label={label} help={help}>
      <div className="grid grid-cols-1 gap-2">
        {rangeMode ? (
          <RangeInput
            label="Dungeon Token range"
            help="Minimum and maximum Dungeon Tokens."
            value={currentRange}
            onChange={range => onChange({ [DUNGEON_TOKEN_KEY]: range })}
          />
        ) : (
          <label className="grid grid-cols-[150px_1fr] items-center gap-2 text-sm">
            <span className="text-[hsl(var(--muted-foreground))]">Dungeon Tokens</span>
            <input
              type="number"
              min="0"
              value={currentAmount}
              onChange={e => onChange({ [DUNGEON_TOKEN_KEY]: parseInt(e.target.value || 0) })}
              className={inputClass}
            />
          </label>
        )}
      </div>
    </FieldWrap>
  );
}
function KeyValue({ label = "", help = "", values, onChange } = {}) {
  return <FieldWrap label={label} help={help}><textarea rows={3} value={Object.entries(values || {}).map(([k, v]) => `${k}:${v}`).join(", ")} onChange={e => onChange(parseKeyValues(e.target.value, false))} className={inputClass} /></FieldWrap>;
}
function KeyRange({ label = "", help = "", values, onChange } = {}) {
  return <FieldWrap label={label} help={help}><textarea rows={3} value={Object.entries(values || {}).map(([k, v]) => `${k}:${(v || [0, 0]).join("-")}`).join(", ")} onChange={e => onChange(parseKeyValues(e.target.value, true))} className={inputClass} /></FieldWrap>;
}
function parseKeyValues(text, range) {
  const out = {};
  text.split(",").map(p => p.trim()).filter(Boolean).forEach(part => {
    const [key, raw = "0"] = part.split(":").map(x => x.trim());
    if (!key) return;
    out[key] = range ? raw.split("-").map(n => parseInt(n || 0)) : parseInt(raw || 0);
  });
  return out;
}
function BalanceCard({ label = "", value, onChange } = {}) {
  return (
    <div className="border border-[hsl(var(--border))] rounded p-3 space-y-3">
      <div className="label-eyebrow mb-2">{label}</div>
      <RangeInput label="Number of stats" help="Minimum and maximum stat lines this rarity can roll on this gear slot." value={value.stat_count || [1, 1]} onChange={stat_count => onChange({ ...value, stat_count })} />
      <NumberInput label="Minimum stat value" help="Lowest bonus each rolled stat can get." value={value.stat_min ?? 1} onChange={stat_min => onChange({ ...value, stat_min })} />
      <NumberInput label="Maximum stat value" help="Highest bonus each rolled stat can get." value={value.stat_max ?? 1} onChange={stat_max => onChange({ ...value, stat_max })} />
    </div>
  );
}
function EncounterBalance({ label = "", value, onChange } = {}) {
  return <div className="border border-[hsl(var(--border))] rounded p-3"><div className="label-eyebrow mb-2">{label}</div><RangeInput label="DC range" help="" value={value.dc || [5, 8]} onChange={dc => onChange({ ...value, dc })} /><RangeInput label="Damage range" help="" value={value.damage || [0, 5]} onChange={damage => onChange({ ...value, damage })} /></div>;
}

function SimpleCrud({ collection, columns, blank }) {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get(`/admin/${collection}`); setRows(data); }, [collection]);
  useEffect(() => { load(); }, [load]);
  const save = async (row) => {
    const { id, _id, ...payload } = row;
    await api.patch(`/admin/${collection}/${id || _id}`, payload);
    toast.success("Saved");
    load();
  };
  const create = async () => {
    const payload = { ...adding };
    delete payload.id;
    await api.post(`/admin/${collection}`, payload);
    setAdding(null);
    toast.success("Created");
    load();
  };
  return (
    <div className="space-y-3 max-w-6xl">
      <button onClick={() => setAdding({ ...blank })} className="text-sm uppercase tracking-widest border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-5 py-3 rounded flex items-center gap-2"><Plus className="w-4 h-4" /> Add</button>
      {adding && <div className="surface p-4 grid grid-cols-1 md:grid-cols-3 gap-3">{columns.map(c => <SimpleField key={c.key} col={c} value={adding[c.key]} onChange={v => setAdding({ ...adding, [c.key]: v })} />)}<button onClick={create} className="bg-[hsl(var(--primary))] text-white px-4 py-2 rounded">Create</button></div>}
      {rows.map(row => <SimpleRow key={row.id || row._id} row={row} columns={columns} onSave={save} />)}
    </div>
  );
}
function SimpleRow({ row, columns, onSave }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => setDraft(row), [row]);
  return <div className="surface p-4 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 items-end">{columns.map(c => <SimpleField key={c.key} col={c} value={draft[c.key]} onChange={v => setDraft({ ...draft, [c.key]: v })} />)}<button onClick={() => onSave(draft)} className="border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-4 py-2 rounded">Save</button></div>;
}
function SimpleField({ col, value, onChange }) {
  if (col.type === "number") return <NumberInput label={col.label} help="" value={value} onChange={onChange} />;
  if (col.type === "float") return <NumberInput label={col.label} help="" value={value} step="0.001" onChange={onChange} />;
  if (col.type === "color") return <FieldWrap label={col.label} help=""><input type="color" value={value || "#9CA3AF"} onChange={e => onChange(e.target.value)} className={`${inputClass} h-10 p-1`} /></FieldWrap>;
  return <Input label={col.label} help="" value={value} onChange={onChange} />;
}

